import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { fmtEur } from '../../lib/utils';
import { isoDay, isSuperAdmin, type Entity } from '../../features/admin/adminApi';

/**
 * Movimento in contanti (incasso o uscita): solo l'essenziale.
 * Controparte, descrizione, importo, data, già incassato/pagato (default sì).
 * L'entità (Masamune / personale) la sceglie solo il Super Admin.
 * Salva con Ctrl/Cmd+Invio.
 */
export function NewCashEntryModal({ onClose, direction = 'IN' }: { onClose: () => void; direction?: 'IN' | 'OUT' }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const isIn = direction === 'IN';
  const [form, setForm] = useState({
    counterparty: '',
    description: '',
    amount: '',
    date: isoDay(),
    done: true,
    entity: 'MASAMUNE' as Entity,
  });
  const amount = Number(String(form.amount).replace(',', '.'));

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/admin/cash-entries', {
      direction,
      counterparty: form.counterparty.trim(),
      description: form.description.trim() || null,
      amountNet: amount,
      vatRate: 0,
      amountGross: amount,
      expectedDate: form.date,
      status: form.done ? 'PAGATO' : 'PREVISTO',
      paidAt: form.done ? form.date : null,
      method: 'CONTANTI',
      entity: form.entity,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast.ok(isIn ? 'Incasso in contanti registrato' : 'Uscita in contanti registrata', `${form.counterparty} · € ${fmtEur(amount, true)}`);
      onClose();
    },
    onError: (err) => toast.alert('Non salvato', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = !!form.counterparty.trim() && Number.isFinite(amount) && amount > 0 && !!form.date && !create.isPending;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); create.mutate(); }
  };

  return (
    <Modal
      title={isIn ? 'Incasso in contanti' : 'Uscita in contanti'}
      kicker="Amministrazione"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
            {isIn ? 'Registra incasso' : 'Registra uscita'} <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field label={isIn ? 'Chi paga' : 'A chi'} required span={12}>
          <input className="inp" autoFocus placeholder={isIn ? 'Cliente' : 'Fornitore, persona'} value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} />
        </Field>
        <Field label="Importo" required span={6}>
          <input className="inp mono" inputMode="decimal" placeholder="0,00" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </Field>
        <Field label="Data" required span={6}>
          <input className="inp mono" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </Field>
        <Field label="Per cosa" span={12} hint="Facoltativo">
          <input className="inp" placeholder={isIn ? 'Es. saldo sito, servizio una tantum' : 'Es. pulizie settembre, manutenzione'} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <Field label={isIn ? 'Già incassato' : 'Già pagato'} span={isSuperAdmin(me) ? 6 : 12}>
          <div className="seg-control">
            <button type="button" aria-pressed={form.done} onClick={() => setForm({ ...form, done: true })}>Sì</button>
            <button type="button" aria-pressed={!form.done} onClick={() => setForm({ ...form, done: false })}>Previsto</button>
          </div>
        </Field>
        {isSuperAdmin(me) && (
          <Field label="Di chi" span={6}>
            <div className="seg-control">
              <button type="button" aria-pressed={form.entity === 'MASAMUNE'} onClick={() => setForm({ ...form, entity: 'MASAMUNE' })}>Masamune</button>
              <button type="button" aria-pressed={form.entity === 'PERSONALE_ANDREA'} onClick={() => setForm({ ...form, entity: 'PERSONALE_ANDREA' })}>Personale</button>
            </div>
          </Field>
        )}
      </div>
    </Modal>
  );
}
